import { RefObject, useEffect } from "react";

import { Column, RowData } from "@tanstack/react-table";

import { TST_BLESSED_ID_COLUMN_SELECTION } from "../consts";
import { AdjustableColumns } from "../utils/getAdjustableColumns";

import { useContainerWidth } from "./useContainerWidth";

const MIN_UNPINNED_WIDTH = 250;

interface PinnedWidthBudgetInput<T extends RowData> {
    /** Scroll container whose width bounds the pinned strip. */
    containerRef: RefObject<HTMLElement | null> | undefined;
    /** Forces a re-measure when it changes (e.g. the dropdown opening). */
    reattachKey?: unknown;
    /** Current column layout; the pinned strip and selection column are read. */
    layout: AdjustableColumns<T>;
    /** Called with the rightmost pins that overflow the budget after a resize. */
    onOverflow: (columns: Array<Column<T>>) => void;
}

export interface PinnedWidthBudget<T extends RowData> {
    canFitPin: (column: Column<T>) => boolean;
}

export function usePinnedWidthBudget<T extends RowData>({
    containerRef,
    reattachKey,
    layout,
    onOverflow,
}: PinnedWidthBudgetInput<T>): PinnedWidthBudget<T> {
    const { pinnedColumns, pinnedWidth, selectionColumn } = layout;
    const availableWidth = useContainerWidth(containerRef, reattachKey);
    const selectionPinExtra = computeSelectionPinExtra(selectionColumn);

    const canFitPin = (column: Column<T>) =>
        computeCanFitPin({
            availableWidth,
            pinnedWidth,
            columnWidth: column.getSize(),
            selectionPinExtra,
        });

    // Drops the rightmost overflowing pins and hand them to the caller to apply.
    useEffect(() => {
        const dropped = computeOverflowDrop({
            availableWidth,
            pinnedWidth,
            pinnedColumns,
        });
        if (dropped.length === 0) {
            return;
        }
        onOverflow(dropped);
    }, [availableWidth, pinnedWidth, pinnedColumns, onOverflow]);

    return { canFitPin };
}

// Computes the extra width to reserve for the selection column, which auto-pins
// alongside the first pin. Zero unless it exists, can pin, and isn't pinned yet.
export function computeSelectionPinExtra<T extends RowData>(
    selectionColumn: Column<T> | undefined
): number {
    const needsAutoPin =
        selectionColumn !== undefined &&
        selectionColumn.getCanPin() &&
        selectionColumn.getIsPinned() !== "left";
    return needsAutoPin ? selectionColumn.getSize() : 0;
}

// Whether pinning a `columnWidth`-wide column would still leave room for the
// scrollable region. Without a measured container the constraint is not
// enforced, so anything fits.
export function computeCanFitPin({
    availableWidth,
    pinnedWidth,
    columnWidth,
    selectionPinExtra,
}: {
    availableWidth: number | undefined;
    pinnedWidth: number;
    columnWidth: number;
    selectionPinExtra: number;
}): boolean {
    if (availableWidth === undefined) {
        return true;
    }
    const projectedWidth = pinnedWidth + columnWidth + selectionPinExtra;
    return projectedWidth <= availableWidth - MIN_UNPINNED_WIDTH;
}

// The rightmost (non-selection) pinned columns that must be dropped for the
// pinned strip to fit the budget again, dropped-first order. Empty when the
// strip already fits or the container width can't be measured. The selection
// column is never droppable.
export function computeOverflowDrop<T extends RowData>({
    availableWidth,
    pinnedWidth,
    pinnedColumns,
}: {
    availableWidth: number | undefined;
    pinnedWidth: number;
    pinnedColumns: Array<Column<T>>;
}): Array<Column<T>> {
    if (availableWidth === undefined) {
        return [];
    }
    const limit = availableWidth - MIN_UNPINNED_WIDTH;
    if (pinnedWidth <= limit) {
        return [];
    }
    const droppable = pinnedColumns.filter(
        (column) => column.id !== TST_BLESSED_ID_COLUMN_SELECTION
    );
    return [...droppable].reverse().reduce<{
        width: number;
        dropped: Array<Column<T>>;
    }>(
        (acc, column) =>
            acc.width <= limit
                ? acc
                : {
                      width: acc.width - column.getSize(),
                      dropped: [...acc.dropped, column],
                  },
        { width: pinnedWidth, dropped: [] }
    ).dropped;
}
